import { useState } from "react";

import useVehicleSingle from "../../data/useVehicleSingle";
import Modal from "../../layouts/Modal";
import { FinancialDecision, IVehicle } from "../../models/Vehicle";
import PriceFormat from "../../utils/priceFormat";
import LoadingWheel from "../shared/LoadingWheel";
import VehicleFinancialSummary from "./VehicleFinancialSummary";

const StatRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{label}</span>
    <span className="text-sm font-medium text-gray-900">{value}</span>
  </div>
);

const decisionBadge = (decision: FinancialDecision): { label: string; classes: string } => {
  switch (decision) {
    case FinancialDecision.Target:
      return { label: "Target", classes: "bg-green-100 text-green-800" };
    case FinancialDecision.Rejected:
      return { label: "Rejected", classes: "bg-red-100 text-red-800" };
    case FinancialDecision.Purchased:
      return { label: "Purchased", classes: "bg-blue-100 text-blue-800" };
    default:
      return { label: "None", classes: "bg-gray-100 text-gray-700" };
  }
};

export default function VehicleFinancialStatsCell({ vehicle }: { vehicle: IVehicle }) {
  const [open, setOpen] = useState(false);
  // Only fetch the full vehicle (parts, prices, decision) when the modal is open.
  const { vehicle: vehicleQuery, lead: leadQuery } = useVehicleSingle(open ? vehicle.id : undefined);

  // Parts value/sold are monetary sums; show a dash when there's nothing yet.
  const money = (value?: number | null) => (value != null && value > 0 ? PriceFormat(value) : "—");
  const purchasePrice = vehicle.leads?.[0]?.priceWithVAT;
  const badge = decisionBadge(vehicle.financialDecision ?? FinancialDecision.None);

  return (
    <>
      <Modal open={open} setOpen={setOpen} width="sm:max-w-md">
        {vehicleQuery.isLoading || !vehicleQuery.data?.data ? (
          <div className="flex justify-center py-10"><LoadingWheel /></div>
        ) : (
          <VehicleFinancialSummary vehicle={vehicleQuery.data.data} lead={leadQuery.data?.data ?? null} />
        )}
      </Modal>

      <div className="w-44 space-y-1.5">
        <StatRow label="Target Price" value={vehicle.targetPrice != null ? PriceFormat(vehicle.targetPrice) : "—"} />
        <StatRow label="Purchase Price" value={purchasePrice != null ? PriceFormat(purchasePrice) : "—"} />
        <StatRow label="Parts Value" value={money(vehicle.availablePartValue)} />
        <StatRow label="Parts Sold" value={money(vehicle.soldPartValue)} />
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          className="mt-1 w-full rounded-md border border-primary-600 px-2 py-1 text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-50"
        >
          Financial Summary
        </button>
        <span className={`mt-1 inline-flex w-full items-center justify-center rounded-full px-2 py-0.5 text-xs font-semibold ${badge.classes}`}>
          {badge.label}
        </span>
      </div>
    </>
  );
}
